import { useEffect, useMemo, useState } from 'react';
import AdminUserPicker from './AdminUserPicker';
import { CENTRAL_CCD_ROLE, effectiveAccessEntries, effectivePrincipalCount } from './admin-access-model';
import { changeAdminRoleAssignment, getAdminCcdApprovers, getAdminRoleProjection, reconcileAdminHcRoles, saveAdminDirectUpdaterScope } from './workflow-api';
import './admin-role-management.css';

const MANAGED_ROLES = [
  ['ProfilePhoto.Support', 'Support'],
  ['ProfilePhoto.Admin', 'Admin'],
  ['ProfilePhoto.PhotoLibrary', 'Photo Library'],
];

function writerStatusLabel(status) {
  if (status === 'enabled') return 'Enabled';
  if (status === 'disabled') return 'Provisioned / disabled';
  return 'Not provisioned';
}

function roleDetails(role, security = {}) {
  const genericActive = security.genericRoleManagementStatus === 'enabled' && security.adminWritesEnabled === true;
  const hcActive = security.hcRoleSynchronizationStatus === 'enabled';
  if (role === 'ProfilePhoto.PhotoLibrary') return {
    label: 'Photo Library', management: genericActive ? 'Admin → Access & roles' : 'Microsoft Entra until Generic Role Writer is enabled',
    description: 'Browse and download the latest original and applied employee photos across all companies. Does not grant approval or update permissions.',
  };
  if (role === CENTRAL_CCD_ROLE) return {
    label: 'Central CCD approver', management: 'Admin → Central CCD Approvers', centralCcd: true,
    description: 'Authoritative CCD approval permission. Pending CCD delivery, reminders, dashboard visibility and approval actions use this list.',
  };
  if (role === 'ProfilePhoto.SuperAdmin') return {
    label: 'SuperAdmin', management: 'Microsoft Entra only',
    description: 'Protected full-administration role. It is never assignable or removable from this web app.',
  };
  if (role === 'ProfilePhoto.Admin') return {
    label: 'Admin', management: genericActive ? 'Admin → Access & roles' : 'Microsoft Entra until Generic Role Writer is enabled',
    description: 'Grants the normal Admin workspace permissions.',
  };
  if (role === 'ProfilePhoto.Support') return {
    label: 'Support', management: genericActive ? 'Admin → Access & roles' : 'Microsoft Entra until Generic Role Writer is enabled',
    description: 'Grants workflow support access.',
  };
  if (role === 'ProfilePhoto.DirectUpdater') return {
    label: 'Direct updater', management: genericActive ? 'Admin → Access & roles' : 'Microsoft Entra until Generic Role Writer is enabled',
    description: 'Grants the direct photo-update workflow where authorized.',
  };
  if (role === 'HC.Approver') return {
    label: 'HC approver',
    management: hcActive ? 'Automatic from Companies & approvals' : 'Company route + Microsoft Entra until HC Role Writer is enabled',
    description: hcActive
      ? 'Adding or removing HC from an enabled company route or Central fallback automatically synchronizes the direct HC.Approver role.'
      : 'Effective HC approval currently requires both routing membership and the HC.Approver Entra role.',
  };
  if (role === 'CCD.Approver') return {
    label: 'Legacy CCD approver', management: 'Do not assign', legacy: true,
    description: 'Legacy Entra assignment only. Central CCD Approvers in Admin is the authoritative CCD permission source.',
  };
  return { label: role || 'Unknown role', management: 'Check Microsoft Entra', description: 'Recognized project role assignment.' };
}

function normalized(value) {
  return String(value || '').trim().toLowerCase();
}

export default function AdminRoleAssignments({ getAccessToken, security = {}, companies = [], editing = false, onDirtyChange, onBusyChange, externallyBusy = false }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(MANAGED_ROLES[0][0]);
  const [action, setAction] = useState('assign');
  const [reason, setReason] = useState('');
  const [mutationBusy, setMutationBusy] = useState(false);
  const [mutationMessage, setMutationMessage] = useState('');
  const [hcReason, setHcReason] = useState('');
  const [hcBusy, setHcBusy] = useState(false);
  const [hcMessage, setHcMessage] = useState('');
  const [scopeEmail, setScopeEmail] = useState('');
  const [scopeAction, setScopeAction] = useState('assign');
  const [scopeMode, setScopeMode] = useState('all');
  const [scopeCompanies, setScopeCompanies] = useState([]);
  const [scopeReason, setScopeReason] = useState('');
  const [scopeBusy, setScopeBusy] = useState(false);
  const [scopeMessage, setScopeMessage] = useState('');
  const [accessTab, setAccessTab] = useState('direct');

  const genericStatus = security.genericRoleManagementStatus || 'not_provisioned';
  const hcStatus = security.hcRoleSynchronizationStatus || 'not_provisioned';
  const writerActive = genericStatus === 'enabled' && security.adminWritesEnabled === true;
  const hcWriterActive = hcStatus === 'enabled' && security.adminWritesEnabled === true;
  const mutationUnlocked = writerActive && editing;
  const hcUnlocked = hcWriterActive && editing;
  const emailReady = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email.trim());
  const reasonReady = reason.trim().length >= 5 && reason.trim().length <= 500;
  const hcReasonReady = hcReason.trim().length >= 5 && hcReason.trim().length <= 500;
  const scopeEmailReady = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(scopeEmail.trim());
  const scopeReasonReady = scopeReason.trim().length >= 5 && scopeReason.trim().length <= 500;
  const scopeReady = scopeAction === 'remove' || scopeMode === 'all' || scopeCompanies.length > 0;
  const operationBusy = mutationBusy || hcBusy || scopeBusy || externallyBusy;
  const dirty = Boolean(email || reason || hcReason || scopeEmail || scopeReason || scopeAction !== 'assign' || scopeMode !== 'all' || scopeCompanies.length || role !== MANAGED_ROLES[0][0] || action !== 'assign');
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange?.(mutationBusy || hcBusy || scopeBusy); }, [mutationBusy, hcBusy, scopeBusy, onBusyChange]);
  useEffect(() => {
    if (!dirty && !mutationBusy && !hcBusy && !scopeBusy) return undefined;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty, mutationBusy, hcBusy, scopeBusy]);

  const availableCompanies = useMemo(() => [...new Set((Array.isArray(companies) ? companies : [])
    .map((entry) => String(entry?.companyKey || '').trim())
    .filter(Boolean))].sort((left, right) => left.localeCompare(right)), [companies]);

  async function load() {
    setBusy(true); setError('');
    try {
      const [roles, ccd] = await Promise.all([
        getAdminRoleProjection(getAccessToken),
        getAdminCcdApprovers(getAccessToken),
      ]);
      setResult({ ...roles, ccdApprovers: ccd?.ccdApprovers || null });
    } catch (failure) {
      setError(failure.status === 404 ? 'The access-management API has not been deployed yet.' : failure.message || 'Access lookup failed.');
    } finally { setBusy(false); }
  }

  async function reconcileHc() {
    if (!hcUnlocked || !hcReasonReady || operationBusy) return;
    if (!window.confirm('Synchronize direct HC.Approver assignments with all enabled company routes and Central fallback HC?')) return;
    setHcBusy(true); setHcMessage(''); setError('');
    try {
      const response = await reconcileAdminHcRoles(hcReason.trim(), getAccessToken);
      setHcMessage(`${response.message} Desired: ${response.desiredCount}; added: ${response.addedCount}; already assigned: ${response.alreadyAssignedCount}; removed stale: ${response.removedStaleCount}.`);
      setHcReason('');
      await load();
    } catch (failure) {
      setError(failure.message || 'HC role reconciliation failed.');
    } finally { setHcBusy(false); }
  }

  async function mutate() {
    if (!mutationUnlocked || !emailReady || !reasonReady || operationBusy) return;
    const verb = action === 'assign' ? 'Assign' : 'Remove';
    if (!window.confirm(`${verb} ${role} ${action === 'assign' ? 'to' : 'from'} ${email.trim()}?`)) return;
    setMutationBusy(true); setMutationMessage(''); setError('');
    try {
      const response = await changeAdminRoleAssignment(email.trim(), role, action, reason.trim(), getAccessToken);
      setMutationMessage(response.message || 'Role change completed.');
      setReason('');
      setEmail(''); setRole(MANAGED_ROLES[0][0]); setAction('assign');
      await load();
    } catch (failure) {
      setError(failure.message || 'Role change failed.');
    } finally { setMutationBusy(false); }
  }

  async function saveScope() {
    if (!writerActive || !editing || !scopeEmailReady || !scopeReasonReady || !scopeReady || operationBusy) return;
    const selected = scopeMode === 'companies' ? scopeCompanies : [];
    const description = scopeAction === 'remove' ? 'remove Direct Updater access' : `grant access to ${selected.length ? selected.join(', ') : 'all companies'}`;
    if (!window.confirm(`${description} for ${scopeEmail.trim()}?`)) return;
    setScopeBusy(true); setScopeMessage(''); setError('');
    try {
      let response;
      if (scopeAction === 'remove') {
        await changeAdminRoleAssignment(scopeEmail.trim(), 'ProfilePhoto.DirectUpdater', 'remove', scopeReason.trim(), getAccessToken);
        response = await saveAdminDirectUpdaterScope(scopeEmail.trim(), [], result?.directUpdaterScopes?.version ?? null, scopeReason.trim(), getAccessToken);
        setResult((current) => ({ ...current, directUpdaterScopes: response.directUpdaterScopes }));
      } else {
        response = await saveAdminDirectUpdaterScope(scopeEmail.trim(), selected, result?.directUpdaterScopes?.version ?? null, scopeReason.trim(), getAccessToken);
        setResult((current) => ({ ...current, directUpdaterScopes: response.directUpdaterScopes }));
        await changeAdminRoleAssignment(scopeEmail.trim(), 'ProfilePhoto.DirectUpdater', 'assign', scopeReason.trim(), getAccessToken);
      }
      setScopeMessage(scopeAction === 'remove'
        ? 'Direct Updater access was removed.'
        : response.message || 'Direct Updater access and scope were saved.');
      setScopeEmail(''); setScopeAction('assign'); setScopeMode('all'); setScopeCompanies([]); setScopeReason('');
    } catch (failure) {
      setError(failure.message || 'Direct Updater scope update failed.');
    } finally { setScopeBusy(false); }
  }

  function toggleScopeCompany(company) {
    setScopeCompanies((current) => current.includes(company)
      ? current.filter((entry) => entry !== company)
      : [...current, company]);
  }

  const entries = useMemo(() => effectiveAccessEntries(result?.assignments, result?.ccdApprovers?.approvers), [result]);
  const uniquePrincipals = effectivePrincipalCount(entries);
  const legacyAssignments = entries.filter(entry => roleDetails(entry.role, security).legacy).length;
  const centralCcdCount = entries.filter(entry => entry.role === CENTRAL_CCD_ROLE).length;
  const selectedIdentity = accessTab === 'direct' ? scopeEmail : email;
  const selectedEmail = normalized(selectedIdentity);
  const selectedEntries = selectedEmail ? entries.filter((entry) => normalized(entry.email) === selectedEmail) : [];
  const directEntry = selectedEntries.find((entry) => entry.role === 'ProfilePhoto.DirectUpdater');
  const directRestriction = selectedEmail
    ? (result?.directUpdaterScopes?.restricted || []).find((entry) => normalized(entry.email) === selectedEmail)
    : null;
  const managedSelectedRoles = selectedEntries.filter((entry) => MANAGED_ROLES.some(([value]) => value === entry.role));
  const selectedDisplay = selectedEntries[0]?.name || selectedIdentity || '';

  return <section className="admin-settings-panel admin-role-assignments-panel access-management-shell">
    <div className="access-management-header">
      <div>
        <p className="access-management-kicker">Administration</p>
        <h4>Access Management</h4>
        <p>Manage application roles and Direct Updater access without changing the underlying permission model.</p>
      </div>
      <button className="access-refresh-button" onClick={load} disabled={busy || operationBusy}>{busy ? 'Loading access…' : 'Refresh access'}</button>
    </div>

    <div className={`access-edit-banner ${editing ? 'enabled' : 'locked'}`} role="status">
      <div className="access-edit-icon" aria-hidden="true">{editing ? '✓' : '!'}</div>
      <div>
        <strong>{editing ? 'Editing enabled' : 'Editing is currently locked'}</strong>
        <span>{editing ? 'Changes can be applied and will require an audit reason.' : 'Use Enable editing at the top of the Admin workspace before making access changes.'}</span>
      </div>
      <span className={writerActive ? 'access-writer-pill ready' : 'access-writer-pill'}>Generic Role Writer: {writerStatusLabel(genericStatus)}</span>
    </div>

    <div className="access-management-tabs" role="tablist" aria-label="Access management modes">
      <button type="button" role="tab" aria-selected={accessTab === 'roles'} className={accessTab === 'roles' ? 'active' : ''} onClick={() => setAccessTab('roles')}>
        <span className="access-tab-icon" aria-hidden="true">◎</span> Application Roles
      </button>
      <button type="button" role="tab" aria-selected={accessTab === 'direct'} className={accessTab === 'direct' ? 'active' : ''} onClick={() => setAccessTab('direct')}>
        <span className="access-tab-icon" aria-hidden="true">◉</span> Direct Updater Scope
      </button>
    </div>

    <div className="access-management-grid">
      <div className="access-primary-card">
        {accessTab === 'direct' ? <>
          <div className="access-card-heading">
            <div><h5>Manage Direct Updater access</h5><p>Grant or remove Direct Updater access and control which companies the user can access.</p></div>
          </div>
          {!writerActive && <p className="access-inline-warning">Direct Updater changes are locked until the Generic Role Writer and Admin writes are enabled.</p>}
          <fieldset className="access-step-form" disabled={!writerActive || !editing || operationBusy}>
            <div className="access-step full">
              <span className="access-step-number">1</span>
              <div className="access-step-body">
                <strong>Select user</strong>
                <small>Search Microsoft Entra and select exactly one user.</small>
                <AdminUserPicker
                  value={scopeEmail}
                  onChange={setScopeEmail}
                  getAccessToken={getAccessToken}
                  multiple={false}
                  disabled={!writerActive || !editing || operationBusy}
                  label="Direct Updater user"
                  placeholder="Search user by name or email"
                  help="The role and company scope are managed together."
                />
              </div>
            </div>

            <div className="access-step full">
              <span className="access-step-number">2</span>
              <div className="access-step-body">
                <strong>Configure access</strong>
                <small>Choose the action and company scope.</small>
                <div className="access-form-row">
                  <label>Action<select value={scopeAction} onChange={event => setScopeAction(event.target.value)}><option value="assign">Grant access</option><option value="remove">Remove access</option></select></label>
                  {scopeAction === 'assign' && <label>Access scope<select value={scopeMode} onChange={event => { setScopeMode(event.target.value); if (event.target.value === 'all') setScopeCompanies([]); }}>
                    <option value="all">All companies</option>
                    <option value="companies">Selected companies only</option>
                  </select></label>}
                </div>
                {scopeAction === 'assign' && scopeMode === 'companies' && <div className="admin-direct-scope-companies" role="group" aria-label="Allowed companies">
                  {availableCompanies.map((company) => <label key={company}><input type="checkbox" checked={scopeCompanies.includes(company)} onChange={() => toggleScopeCompany(company)} /> <span>{company}</span></label>)}
                  {!availableCompanies.length && <small>No configured companies are available.</small>}
                </div>}
                {scopeAction === 'assign' && scopeMode === 'companies' && !scopeCompanies.length && <small className="admin-validation-error">Select at least one company.</small>}
              </div>
            </div>

            <div className="access-step full">
              <span className="access-step-number">3</span>
              <div className="access-step-body">
                <strong>Audit reason</strong>
                <small>Provide a reason for this access change.</small>
                <label className="admin-role-reason"><textarea rows="3" maxLength="500" value={scopeReason} onChange={event => setScopeReason(event.target.value)} placeholder="Why is this Direct Updater scope changing?" /></label>
                <div className="access-textarea-meta"><span>{scopeReason.length > 0 && !scopeReasonReady ? 'Enter 5–500 characters.' : ''}</span><span>{scopeReason.length}/500</span></div>
              </div>
            </div>

            <div className="access-step full apply-step">
              <span className="access-step-number">4</span>
              <div className="access-step-body">
                <strong>Apply</strong>
                <small>Review the selected user, access action and scope before saving.</small>
                {scopeMessage && <small className="admin-save-success">{scopeMessage}</small>}
                <button className="access-primary-action" type="button" onClick={saveScope} disabled={!writerActive || !editing || !scopeEmailReady || !scopeReasonReady || !scopeReady || operationBusy}>{scopeBusy ? 'Applying access…' : scopeAction === 'remove' ? 'Remove Direct Updater access' : 'Save access'}</button>
              </div>
            </div>
          </fieldset>

          <div className="access-restricted-list">
            <div className="access-subheading"><strong>Restricted Direct Updaters</strong><span>{(result?.directUpdaterScopes?.restricted || []).length}</span></div>
            {(result?.directUpdaterScopes?.restricted || []).map((entry) => <div key={entry.objectId}><span><strong>{entry.name || entry.email || entry.objectId}</strong><small>{entry.email || ''}</small></span><small>{entry.companyKeys.join(', ')}</small></div>)}
            {result && !(result?.directUpdaterScopes?.restricted || []).length && <small>No restricted users. Other Direct Updater role holders have access to all companies.</small>}
          </div>
        </> : <>
          <div className="access-card-heading">
            <div><h5>Manage application roles</h5><p>Assign or remove the Support and Admin roles. Direct Updater access is managed in its dedicated tab.</p></div>
          </div>
          {!writerActive && <p className="access-inline-warning">Role changes are locked until the Generic Role Writer and controlled Admin writes are enabled.</p>}
          <fieldset className="access-step-form" disabled={!mutationUnlocked || operationBusy}>
            <div className="access-step full">
              <span className="access-step-number">1</span>
              <div className="access-step-body">
                <strong>Select user</strong>
                <small>Search Microsoft Entra and select exactly one user.</small>
                <AdminUserPicker
                  value={email}
                  onChange={setEmail}
                  getAccessToken={getAccessToken}
                  multiple={false}
                  disabled={!mutationUnlocked || operationBusy}
                  label="Target user"
                  placeholder="Search user by name or email"
                  help="Select the user whose application role should change."
                />
              </div>
            </div>
            <div className="access-step full">
              <span className="access-step-number">2</span>
              <div className="access-step-body">
                <strong>Choose role change</strong>
                <small>Select the role and whether to assign or remove it.</small>
                <div className="access-form-row">
                  <label>Role<select value={role} onChange={event => setRole(event.target.value)}>{MANAGED_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  <label>Action<select value={action} onChange={event => setAction(event.target.value)}><option value="assign">Assign</option><option value="remove">Remove</option></select></label>
                </div>
              </div>
            </div>
            <div className="access-step full">
              <span className="access-step-number">3</span>
              <div className="access-step-body">
                <strong>Audit reason</strong>
                <small>Every role change is recorded with your reason.</small>
                <label className="admin-role-reason"><textarea rows="3" maxLength="500" value={reason} onChange={event => setReason(event.target.value)} placeholder="Why is this role changing?" /></label>
                <div className="access-textarea-meta"><span>{reason.length > 0 && !reasonReady ? 'Enter 5–500 characters.' : ''}</span><span>{reason.length}/500</span></div>
              </div>
            </div>
            <div className="access-step full apply-step">
              <span className="access-step-number">4</span>
              <div className="access-step-body">
                <strong>Apply</strong>
                <small>Confirm the user, role and action before applying.</small>
                {mutationMessage && <small className="admin-save-success">{mutationMessage}</small>}
                <button className="access-primary-action" type="button" onClick={mutate} disabled={!mutationUnlocked || !emailReady || !reasonReady || operationBusy}>{mutationBusy ? 'Applying…' : action === 'assign' ? 'Assign role' : 'Remove role'}</button>
              </div>
            </div>
          </fieldset>
        </>}
      </div>

      <aside className="access-side-column">
        <section className="access-side-card current-access-card">
          <div className="access-side-heading"><span className="access-side-icon" aria-hidden="true">○</span><div><h5>Current access</h5><p>View the selected user's effective access.</p></div></div>
          {!selectedIdentity ? <div className="access-empty-user"><div className="access-avatar-placeholder" aria-hidden="true">◌</div><strong>No user selected</strong><span>Search and select a user to view current access details.</span></div> : <>
            <div className="access-selected-user"><div className="access-avatar-placeholder" aria-hidden="true">{selectedDisplay.slice(0, 1).toUpperCase()}</div><span><strong>{selectedDisplay}</strong><small>{selectedIdentity}</small></span></div>
            {accessTab === 'direct' ? <dl className="access-current-details">
              <div><dt>Direct Updater access</dt><dd>{directEntry ? 'Enabled' : result ? 'Not assigned' : 'Not loaded'}</dd></div>
              <div><dt>Company scope</dt><dd>{directEntry ? (directRestriction?.companyKeys?.length ? directRestriction.companyKeys.join(', ') : 'All companies') : '—'}</dd></div>
              <div><dt>Writer status</dt><dd>{writerStatusLabel(genericStatus)}</dd></div>
            </dl> : <div className="access-role-chips">
              <span className="access-current-label">Application roles</span>
              {managedSelectedRoles.map((entry) => <span className="access-role-chip" key={entry.role}>{roleDetails(entry.role, security).label}</span>)}
              {result && !managedSelectedRoles.length && <small>No managed Support/Admin role assigned.</small>}
              {!result && <small>Refresh access to load current assignments.</small>}
            </div>}
          </>}
        </section>

        <section className="access-side-card">
          <div className="access-side-heading"><span className="access-side-icon info" aria-hidden="true">i</span><div><h5>{accessTab === 'direct' ? 'About Direct Updater access' : 'About application roles'}</h5></div></div>
          <p>{accessTab === 'direct'
            ? 'The Direct Updater role controls access to the Direct Updater workflow. Company scope limits which companies the user can search, update and submit for.'
            : 'Support and Admin are managed here. SuperAdmin remains Microsoft Entra-only, while HC and Central CCD access are managed by their dedicated controls.'}</p>
        </section>
      </aside>
    </div>

    {error && <p role="alert" className="workflow-error">{error}</p>}

    <details className="access-advanced-panel">
      <summary><span>HC role maintenance</span><small>Advanced reconciliation for existing HC assignments</small></summary>
      <div className="access-advanced-content">
        <div className="access-advanced-heading"><div><h5>HC approver role synchronization</h5><p>Repair existing direct HC.Approver assignments. Future HC route changes continue to synchronize automatically when the writer is enabled.</p></div><span className={hcWriterActive ? 'access-writer-pill ready' : 'access-writer-pill'}>HC Role Writer: {writerStatusLabel(hcStatus)}</span></div>
        {!hcWriterActive && <p className="access-inline-warning">HC reconciliation is locked until the dedicated HC Role Writer and Admin writes are enabled.</p>}
        <fieldset disabled={!hcUnlocked || operationBusy}>
          <label className="admin-role-reason">Audit reason<textarea rows="2" maxLength="500" value={hcReason} onChange={event => setHcReason(event.target.value)} placeholder="Why are HC roles being reconciled?" /></label>
          {hcReason.length > 0 && !hcReasonReady && <small className="admin-validation-error">Enter 5–500 characters for the audit reason.</small>}
          {hcMessage && <small className="admin-save-success">{hcMessage}</small>}
          <button type="button" onClick={reconcileHc} disabled={!hcUnlocked || !hcReasonReady || operationBusy}>{hcBusy ? 'Reconciling…' : 'Reconcile HC roles'}</button>
        </fieldset>
      </div>
    </details>

    <details className="access-directory-panel">
      <summary><span>Effective access directory</span><small>{result?.status === 'ready' ? `${entries.length} access entries across ${uniquePrincipals} principals` : 'Refresh access to view current assignments'}</small></summary>
      <div className="access-directory-content">
        <div className="admin-role-authority-note">
          <strong>Authority model</strong>
          <span>HC is controlled from <strong>Companies & approvals</strong>. Central CCD is controlled from <strong>Central CCD Approvers</strong>. Support, Direct Updater and Admin are managed here. <code>ProfilePhoto.SuperAdmin</code> remains Microsoft Entra-only.</span>
        </div>
        {result?.status === 'ready' && <>
          <div className="admin-role-summary" aria-label="Effective access summary">
            <div><strong>{entries.length}</strong><span>Access entries</span></div>
            <div><strong>{uniquePrincipals}</strong><span>Principals</span></div>
            <div><strong>{centralCcdCount}</strong><span>Central CCD</span></div>
            <div><strong>{legacyAssignments}</strong><span>Legacy CCD</span></div>
          </div>
          <div className="admin-role-table-wrap">
            <div className="admin-role-table-row header" aria-hidden="true"><span>User / principal</span><span>Effective access</span><span>Where managed / effective rule</span><span>Status</span></div>
            {entries.map(entry => {
              const details = roleDetails(entry.role, security);
              const state = details.centralCcd ? 'Authoritative' : details.legacy ? 'Legacy / non-authoritative' : entry.roleEnabled ? 'Enabled' : 'Role disabled';
              return <div className="admin-role-table-row" key={`${entry.id}-${entry.role}`}>
                <span data-label="User / principal"><strong>{entry.name || entry.email || 'Unnamed principal'}</strong>{entry.email && <small>{entry.email}</small>}<small>{entry.principalType || 'Unknown principal type'}</small></span>
                <span data-label="Effective access"><strong>{details.label}</strong><code>{details.centralCcd ? 'Central CCD list' : entry.role}</code></span>
                <span data-label="Where managed / effective rule" className="admin-role-description"><strong>{details.management}</strong><small>{details.description}</small></span>
                <span data-label="Status"><span className={`admin-role-state ${details.legacy ? 'legacy' : details.centralCcd || entry.roleEnabled ? 'enabled' : 'disabled'}`}>{state}</span></span>
              </div>;
            })}
            {!entries.length && <p className="admin-routing-empty">No effective Profile Picture Studio access was returned.</p>}
          </div>
          <small className="admin-role-snapshot">Snapshot: {result.generatedAt}</small>
        </>}
        {result && result.status !== 'ready' && <p role="status">{result.message}</p>}
      </div>
    </details>
  </section>;
}
